import type { Bed, FloretPlant, Garden, GardenMember, Note, Photo, Plant, PropertyMap, User, Zone } from '../types';

const BASE = '';

function token() {
  return localStorage.getItem('gm_token');
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const tok = token();
  const isFormData = init.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
    ...(init.headers as Record<string, string> | undefined),
  };
  const res = await fetch(`${BASE}/api${path}`, { ...init, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Request failed');
  }
  return res.json() as Promise<T>;
}

export const api = {
  // Auth
  register: (email: string, password: string) =>
    req<{ token: string; user: User }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  login: (email: string, password: string) =>
    req<{ token: string; user: User }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  me: () => req<User>('/auth/me'),

  // Garden
  getGarden: () => req<{ garden: Garden | null; members: GardenMember[] }>('/garden'),
  createGarden: (name: string) =>
    req<Garden>('/garden', { method: 'POST', body: JSON.stringify({ name }) }),
  joinGarden: (inviteCode: string) =>
    req<{ gardenId: string; name: string }>('/garden/join', {
      method: 'POST',
      body: JSON.stringify({ inviteCode }),
    }),
  regenerateInvite: () =>
    req<{ invite_code: string }>('/garden/regenerate-invite', { method: 'POST' }),

  // Map
  getMap: () => req<PropertyMap>('/map'),
  updateMap: (id: string, data: Partial<PropertyMap>) =>
    req<PropertyMap>(`/map/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  uploadMapImage: (id: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return req<PropertyMap>(`/map/${id}/image`, { method: 'POST', body: fd });
  },

  // Zones
  getZones: () => req<Zone[]>('/zones'),
  createZone: (data: Partial<Zone>) =>
    req<Zone>('/zones', { method: 'POST', body: JSON.stringify(data) }),
  updateZone: (id: string, data: Partial<Zone>) =>
    req<Zone>(`/zones/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteZone: (id: string) => req<{ ok: boolean }>(`/zones/${id}`, { method: 'DELETE' }),

  // Beds
  getBeds: (zoneId?: string) =>
    req<Bed[]>('/beds' + (zoneId ? `?zone_id=${zoneId}` : '')),
  getBed: (id: string) => req<Bed>(`/beds/${id}`),
  createBed: (data: Partial<Bed>) =>
    req<Bed>('/beds', { method: 'POST', body: JSON.stringify(data) }),
  updateBed: (id: string, data: Partial<Bed>) =>
    req<Bed>(`/beds/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteBed: (id: string) => req<{ ok: boolean }>(`/beds/${id}`, { method: 'DELETE' }),

  // Plants
  getPlants: (params?: { bed_id?: string; status?: string; plant_type?: string; q?: string }) => {
    const qs = params ? '?' + new URLSearchParams(
      Object.entries(params).filter(([, v]) => v) as [string, string][]
    ).toString() : '';
    return req<Plant[]>(`/plants${qs}`);
  },
  getPlant: (id: string) => req<Plant>(`/plants/${id}`),
  createPlant: (data: Partial<Plant>) =>
    req<Plant>('/plants', { method: 'POST', body: JSON.stringify(data) }),
  updatePlant: (id: string, data: Partial<Plant>) =>
    req<Plant>(`/plants/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deletePlant: (id: string) => req<{ ok: boolean }>(`/plants/${id}`, { method: 'DELETE' }),

  // Photos
  getPhotos: (entityType: string, entityId: string) =>
    req<Photo[]>(`/photos?entity_type=${entityType}&entity_id=${entityId}`),
  uploadPhoto: (entityType: string, entityId: string, file: File, caption?: string, dateTaken?: string) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('entity_type', entityType);
    fd.append('entity_id', entityId);
    if (caption) fd.append('caption', caption);
    if (dateTaken) fd.append('date_taken', dateTaken);
    return req<Photo>('/photos/upload', { method: 'POST', body: fd });
  },
  deletePhoto: (id: string) => req<{ ok: boolean }>(`/photos/${id}`, { method: 'DELETE' }),
  // Encode each path segment but preserve slashes so the wildcard route matches correctly
  photoUrl: (r2Key: string) => `/api/photos/file/${r2Key.split('/').map(encodeURIComponent).join('/')}`,

  // Notes
  getNotes: (entityType: string, entityId: string) =>
    req<Note[]>(`/notes?entity_type=${entityType}&entity_id=${entityId}`),
  createNote: (data: Partial<Note>) =>
    req<Note>('/notes', { method: 'POST', body: JSON.stringify(data) }),
  updateNote: (id: string, data: Partial<Note>) =>
    req<Note>(`/notes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteNote: (id: string) => req<{ ok: boolean }>(`/notes/${id}`, { method: 'DELETE' }),

  // Floret Library (proxied via our Worker to avoid CORS)
  floretSearch: (q: string) =>
    req<{ products: FloretPlant[] }>(`/floret/suggest?q=${encodeURIComponent(q)}`),
};
